import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { notify } from '../../../utils/toast';
import CreateConnectionForm from '../../connections/channels/components/CreateConnectionForm';
import QRCodeScanner from '../../connections/channels/components/QRCodeScanner';
import { useWhatsAppConnection } from '../../connections/channels/hooks/useWhatsAppConnection';
import { markOnboardingStepSkipped } from '../hooks/useOnboardingSkip';
import OnboardingStepFooter from './OnboardingStepFooter';

export type WhatsAppOnboardingStepProps = {
  /** Limpa a view manual após conectar/pular (ex.: usuário tinha voltado da etapa 2). */
  onAdvance?: () => void;
};

export default function WhatsAppOnboardingStep({ onAdvance }: WhatsAppOnboardingStepProps) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const [isSkipping, setIsSkipping] = useState(false);

  const onSuccess = useCallback(() => {
    window.dispatchEvent(new CustomEvent('crm:onboarding-updated'));
    onAdvance?.();
  }, [onAdvance]);

  const {
    step,
    setStep,
    isLoading,
    qrCode,
    error,
    startConnection,
    cancelConnection,
  } = useWhatsAppConnection(onSuccess);

  useEffect(() => {
    setStep('create');
  }, [setStep]);

  const handleSkip = async () => {
    if (!orgId || isSkipping) return;
    setIsSkipping(true);
    try {
      const ok = await markOnboardingStepSkipped(orgId, 'whatsappSkipped');
      if (ok) onAdvance?.();
      else notify.error('Não foi possível pular esta etapa. Tente novamente.');
    } finally {
      setIsSkipping(false);
    }
  };

  const handleQrCancel = async () => {
    await cancelConnection();
    setStep('create');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {step === 'create' && (
        <CreateConnectionForm
          embedded
          onBack={() => {}}
          onSubmit={startConnection}
          isLoading={isLoading}
          error={error}
        />
      )}

      {step === 'qrcode' && (
        <QRCodeScanner
          embedded
          qrCode={qrCode || ''}
          onCancel={handleQrCancel}
          onRefresh={() => {}}
          isLoading={!qrCode}
        />
      )}

      <OnboardingStepFooter onSkip={handleSkip} isSkipping={isSkipping} />
    </div>
  );
}
